import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HighchartsReactRefObject } from '@highcharts/react'
import { Grid, useMediaQuery, useTheme } from '@mui/material'

import useDates from '../../../../../utils/hooks/useDates'
import useIsMobile from '../../../../../utils/hooks/useIsMobile'

import BaseChart from './base-chart'
import { DEFAULT_CHART_TIME_RANGE, defaultDataProps, getAlertPlotBands, getAlertPlotLines, rangeSelectorIndexMatrix, useFetchChartData } from './helpers'
import classes from './index.styles'

const LineChart = ({
  chart,
  title,
  deviceId,
  channels,
  alerts,
  isSignalChart,
}: {
  chart: string
  title: string
  deviceId: number
  channels: ChannelType[]
  alerts: IAlertHistory[]
  isSignalChart?: boolean
}) => {
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md')) && !isLargeScreen
  const isMobile = useIsMobile()
  const { formatDateTime } = useDates()
  const chartRef = useRef<HighchartsReactRefObject>(null)
  const [selectedRange, setSelectedRange] = useState<ChartTimeRange>(DEFAULT_CHART_TIME_RANGE)
  const { data, isLoading, fetchData } = useFetchChartData(deviceId, channels, chart)
  const plotBands = useMemo(() => getAlertPlotBands({ alerts, channels, chartRef, theme, formatDateTime }), [alerts, channels, chartRef, theme, formatDateTime])
  const plotLines = useMemo(() => getAlertPlotLines({ alerts, channels, theme }), [alerts, channels, theme])

  // Update selected range immediately for button state, then load the corresponding data
  const onRangeBtnClick = useCallback(
    async (timeRange: ChartTimeRange) => {
      setSelectedRange(timeRange)
      chartRef?.current?.chart.showLoading()
      await fetchData(timeRange)
    },
    [fetchData]
  )

  useEffect(() => {
    // Re-apply the selected range after async fetches so an old zoom window does not persist and the active range button stays accurate
    const xAxis = chartRef.current?.chart?.xAxis?.[0]
    if (!xAxis || data.length === 0) return

    const { dataMax } = xAxis.getExtremes()
    if (typeof dataMax !== 'number') return

    const max = dataMax
    let min = max

    if (selectedRange === 'day') {
      min = max - 24 * 60 * 60 * 1000
    } else if (selectedRange === 'week') {
      min = max - 7 * 24 * 60 * 60 * 1000
    } else {
      const monthAgo = new Date(max)
      monthAgo.setMonth(monthAgo.getMonth() - 1)
      min = monthAgo.getTime()
    }

    xAxis.setExtremes(min, max)
  }, [data, selectedRange])

  const chartData = useMemo(() => {
    if (data.length === 0) return null

    return {
      ...defaultDataProps,
      chart: {
        type: 'line',
        events: {
          render: () => {
            chartRef?.current?.chart?.hideLoading()
          },
        },
      },
      title: {
        ...defaultDataProps.title,
        text: title,
      },
      accessibility: {
        description: `${title} chart.`,
      },
      rangeSelector: {
        allButtonsEnabled: true,
        selected: rangeSelectorIndexMatrix[selectedRange],
        buttons: [
          {
            type: 'day',
            count: 1,
            text: '1d',
            title: 'View 1 day',
            events: {
              click: () => onRangeBtnClick('day'),
            },
          },
          {
            type: 'week',
            count: 1,
            text: '1w',
            title: 'View 1 week',
            events: {
              click: () => onRangeBtnClick('week'),
            },
          },
          {
            type: 'month',
            count: 1,
            text: '1m',
            title: 'View 1 month',
            events: {
              click: () => onRangeBtnClick('month'),
            },
          },
        ],
      },
      yAxis:
        isMobile || isMediumScreen
          ? {}
          : data.map((d, i) => ({
              title: {
                text: d.yAxisTitle,
              },
              gridLineWidth: i === 0 ? 1 : 0,
              opposite: i !== 0,
              ...(isSignalChart && {
                reversed: d.unit !== 'dB',
                minorGridLineWidth: 0,
                gridLineWidth: 0,
                ...(d.unit === 'dBm' && {
                  plotBands: [
                    {
                      from: 110,
                      to: 120,
                      color: '#FEEBEE',
                    },
                    {
                      from: 100,
                      to: 110,
                      color: '#FFF3E0',
                    },
                    {
                      from: 85,
                      to: 100,
                      color: '#E8F5E9',
                    },
                    {
                      from: 70,
                      to: 85,
                      color: '#C8E6C9',
                    },
                    {
                      from: 0,
                      to: 70,
                      color: '#A5D6A7',
                    },
                  ],
                }),
              }),
            })),
      xAxis: {
        crosshair: true,
        plotBands,
        plotLines,
      },
      series: [
        ...data.map((d) => ({
          name: d.seriesName,
          type: 'line',
          data: d.data,
          tooltip: {
            valuePrefix: d.unit === 'dBm' ? '-' : '',
            valueSuffix: d.unit,
          },
        })),
      ],
    }
  }, [data, title, selectedRange, isMobile, isMediumScreen, plotBands, plotLines, onRangeBtnClick, isSignalChart])
  return (
    <Grid size={12} sx={classes.baseChartContainer}>
      <BaseChart chartData={chartData as unknown as Highcharts.Options} chartRef={chartRef} isLoading={isLoading} />
    </Grid>
  )
}

export default LineChart
